import detectBrowserLanguage from 'detect-browser-language';
import WebViewCommunicator from '../helpers/WebViewCommunicator';
import { LANGUAGE_CODES } from '../../resources/lang/languages.generated';

export const LANGUAGE_KEY = 'LANGUAGE';

// The platform-wide fallback: every region lists it last, and untranslated
// languages render English copy until their bundle lands.
export const FALLBACK_LANGUAGE = 'en';

// Languages with a translation bundle under src/resources/lang, from the generated
// registry: add a language to scripts/translate-catalog.config.json and re-run the
// script, never here. A partner's primary language in this list is used as-is rather
// than downgraded to English.
export const TRANSLATED_LANGUAGES = LANGUAGE_CODES;

// What to offer when the brand carries no language set at all — a legacy cache entry or
// an unreachable brand-config. Deliberately NOT TRANSLATED_LANGUAGES: those two meant
// the same thing while only en/de were translated, and offering all nine to a brand we
// know nothing about would be a guess, not a default. English first so such a brand
// lands on the fallback, never on German.
export const UNKNOWN_BRAND_LANGUAGES = ['en', 'de'];

// The languages a switcher may offer and i18next accepts. Since PROD-476 every one of
// them ships a bundle, so this is the same generated list as TRANSLATED_LANGUAGES; the
// two names stay because callers ask two different questions of it.
export const SUPPORTED_LANGUAGES = LANGUAGE_CODES;

class LocalizationManager {
  getLanguage() {
    const language = localStorage.getItem(LANGUAGE_KEY);

    return SUPPORTED_LANGUAGES.find((item) => item === language) ?? null;
  }

  saveLanguage(language) {
    localStorage.setItem(LANGUAGE_KEY, language);
    WebViewCommunicator.forwardLanguage(language);
  }

  getLanguageFromUrl() {
    try {
      const url = window.location.href;
      const query = 'lng?=';
      if (url?.includes(query)) {
        const language = url?.split(query)[1];

        return SUPPORTED_LANGUAGES.find((item) => item === language) ?? null;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  getPreferredLanguage() {
    try {
      const preferredBrowserLanguage = detectBrowserLanguage();
      const language = preferredBrowserLanguage?.split(/-|_/)[0];
      const defaultLanguage = SUPPORTED_LANGUAGES.find((item) => item === language);

      if (!defaultLanguage) {
        return null;
      }

      return defaultLanguage;
    } catch (error) {
      return null;
    }
  }

  /**
   * Human-readable name of a language code in the UI language ("Spanish" / "Spanisch"),
   * so language menus need no per-language translation keys.
   */
  getLanguageName(language, displayLanguage = FALLBACK_LANGUAGE) {
    try {
      return new Intl.DisplayNames([displayLanguage], { type: 'language' }).of(language) ?? language.toUpperCase();
    } catch {
      return language.toUpperCase();
    }
  }
}

export default new LocalizationManager();
